import {
  LOAD_CONTEXT,
  RELOAD_CONTEXT,
  PROCESS_RELOAD_CONTEXT,
  PLAY_ACTION,
  MESSAGE_ACTION,
  MESSAGE_HEADER_ACTION,
  WAIT_FOR_USER_INPUT_ACTION,
  ENABLE_INPUT,
  LOAD_NEXT_MODULE_ACTION,
  PLAY_AUDIO,
  PLAY_ACTION_ERROR,
  AUDIO_FINISHED,
  PROCESS_ACTIONS,
  MODULE_SUCCESSFULLY_LOADED,
  MODULE_LOAD_FAILED,
  VALIDATION_FAILED,
  ADD_MESSAGE,
  ADD_HEADER_MESSAGE,
  SUBMIT_ANSWER,
  CLEAR_MESSAGES_ACTION,
  CLEAR_MESSAGES,
  UPDATE_UI,
  SET_UI_ELEMENT,
  LOAD_MESSAGES,
  LOAD_HISTORY,
  LOAD_HISTORY_NEXT_PAGE,
  LOGOUT,
  RECONNECT_ACTIVATE,
  RECONNECT_DEACTIVATE,
  RECONNECT_TRY,
  RECONNECT_WAIT,
  RECONNECT_RETRY_NOW,
  CONNECTION_ONLINE,
  CONNECTION_OFFLINE,
  REDIRECT,
  JUMP,
  REMOVE_AUDIO_MODAL,
  SWITCH_LESSON,
  SWITCH_COURSE,
  SWITCH_QUESTIONNAIRE
} from '../constants';
import config from '../config.json';
import apiClient, { axiosRetryOn } from '../lib/celled_client';
import { CancelToken } from 'axios';
import { isOffline } from '../lib/connection_status';
import { checkCommonErrors, requestBelongsToCurrentSession } from '../lib/auth';
import { getUserId } from './selectors';
import { put, take, call, actionChannel, race, flush, select } from 'redux-saga/effects';
import { delay, buffers } from 'redux-saga';
import _ from 'lodash';
import { browserHistory } from 'react-router';

// --------------------------------------------------------------------
// ----------------------------- HELPERS ------------------------------
// --------------------------------------------------------------------

function getInputType(actionsArray) {
  const enableInput = _.find(actionsArray, {
    type: WAIT_FOR_USER_INPUT_ACTION
  });
  if (enableInput) {
    return { type: enableInput['input-type'], options: enableInput.options };
  } else {
    return false;
  }
}

function hasAutoAnswer(actionsArray) {
  const enableInput = _.find(actionsArray, { type: LOAD_NEXT_MODULE_ACTION });
  if (enableInput) {
    return true;
  } else {
    return false;
  }
}

function shouldEnableInputForAction(action, allActionsToBeProcessed) {
  let shouldEnableInput = _.isBoolean(action.shouldEnableInput) ? action.shouldEnableInput : false;
  return shouldEnableInput ? !hasAutoAnswer(allActionsToBeProcessed) : false;
}

function groupAudios(actions) {
  let results = [];
  actions.forEach(function (action) {
    if (action.type === 'PLAY_ACTION') {
      if (_.isArray(action.url)) {
        results.push(action);
      } else {
        action.url = [action.url];
        results.push(action);
      }
    } else {
      results.push(action);
    }
  });
  return results;
}

/**
 * This method prepares and fetches additional information that the client
 * can send to our server at launch (+ initial context delivery phase)
 */
function getInitialLoadingModuleContext() {
  const loadKey = window.sessionStorage.getItem(LOAD_CONTEXT);
  let isNewAppSession = false;
  if (loadKey === null) {
    window.sessionStorage.setItem(LOAD_CONTEXT, new Date().getTime());
    isNewAppSession = true;
  }

  // using SessionStorage, inform the server if the current instance
  // is representing a brand new tab / app launch
  return { isNewAppSession };
}

// --------------------------------------------------------------------
// ------------------ (REDUX) GENERATOR FUNCTIONS ---------------------
// --------------------------------------------------------------------

// Will process each action from the server and execute them sequentially
export function* processServerActions() {
  // Take all PROCESS_ACTIONS dispatched actions,
  // in case one is already running, it will queue the next

  const queuedActions = yield actionChannel(PROCESS_ACTIONS, yield buffers.expanding());

  while (true) {
    const nextActions = yield take(queuedActions);
    // Iterate through all the actions array
    // and build the generator function that will
    // execute the actions

    try {
      const { logout, jump } = yield race({
        logout: take(LOGOUT),
        jump: take(JUMP),
        processedActions: call(translateActions, nextActions)
      });

      if (jump && jump.payload) {
        yield call(callJump, jump.payload.jumpURL);
      }
      // If user logout clear all actions queue
      if (logout) {
        yield flush(queuedActions);
      }
    } catch (error) {
      // In case an error is catched, reset the function
      // and start listening to new 'queuedActions' actions

      yield call(processServerActions);
    }
  }
}

/**
 * How long to wait before the next attempt: the configured interval, doubling for each failure up
 * to MAXIMUM_RECONNECT_TIMEOUT, so a long outage stops hammering the network.
 *
 * The cap is a constant rather than a config.json key because the deploy scripts regenerate that
 * file, and a key they don't know about would be undefined in every deployed environment.
 */
export const MAXIMUM_RECONNECT_TIMEOUT = 60000;

/**
 * The learner moving on: a request made in the lesson they left is no longer theirs to recover.
 * Replaying it would feed the old lesson's actions into the new one (LMSNEXT-1325).
 */
const LEFT_THE_LESSON = [SWITCH_LESSON, SWITCH_COURSE, SWITCH_QUESTIONNAIRE, PROCESS_RELOAD_CONTEXT];

export function reconnectDelay(attempt) {
  const wait = config.automaticReconnectTimeout * Math.pow(2, attempt);
  return Math.min(wait, MAXIMUM_RECONNECT_TIMEOUT);
}

/**
 * Replays the request that lost the connection until it succeeds (LMSNEXT-1325).
 *
 * The learner is mid-lesson, so the page can't be reloaded without losing their place: the failed
 * request has to be retried underneath them. The banner shows a countdown between attempts
 * ('waiting'), the attempt itself ('reconnecting'), or that the device has no network ('offline').
 *
 * Previously a failed attempt called this generator again, nesting a new loop inside the old one:
 * the banner stayed on 'Reconnecting...' with no countdown and the retries never ended. Now every
 * outcome returns to the top of one loop -- success clears the banner, any failure goes back to the
 * countdown with a longer wait, and a logout drops the pending request.
 *
 * While the device reports no network there is nothing to try, so the loop waits for it to come
 * back rather than spending attempts on requests that cannot leave the device.
 *
 * One request is recovered at a time. A second lesson request that fails while a replay is pending
 * is not queued: replaying several in the order they were made needs the server to tolerate a step
 * being submitted twice, which is the same open question as replaying an answer at all.
 */
export function* reconnectTry() {
  let pendingRequest = null;
  let attempt = 0;
  let offline = false;

  while (true) {
    if (!pendingRequest) {
      const action = yield take([RECONNECT_ACTIVATE, CONNECTION_OFFLINE, CONNECTION_ONLINE, LOGOUT]);
      if (action.type === CONNECTION_OFFLINE) {
        offline = true; // the reducer shows the banner; nothing to replay yet
        continue;
      }
      if (action.type === CONNECTION_ONLINE) {
        offline = false;
        yield put({ type: RECONNECT_DEACTIVATE });
        continue;
      }
      if (action.type === LOGOUT) {
        attempt = 0;
        continue;
      }
      // A request that was in flight when the learner logged out fails afterwards and asks to be
      // replayed. It belongs to a session that has ended, so it is dropped here rather than after a
      // wait -- otherwise the banner sits on the logged-out screen for the length of that wait.
      // The token comparison alone isn't enough: the session-timeout modal logs out without clearing
      // the stored credentials, so a request from that session would still match.
      const loggedIn = yield select((state) => state.mainApp.userIsLogged);
      if (!loggedIn || !(yield call(requestBelongsToCurrentSession, action.payload))) {
        yield put({ type: RECONNECT_DEACTIVATE });
        continue;
      }
      pendingRequest = action;
      attempt = 0;
      // the device may already have been offline when this request failed, in which case there is
      // nothing to count down to -- say so, since the banner would otherwise show a countdown that
      // never reaches an attempt
      offline = yield call(isOffline);
      if (offline) {
        yield put({ type: CONNECTION_OFFLINE });
      }
    }

    if (offline) {
      // No attempt can reach anything: wait for the device instead of timing out over and over.
      const { loggedOut, movedOn } = yield race({
        backOnline: take(CONNECTION_ONLINE),
        loggedOut: take(LOGOUT),
        movedOn: take(LEFT_THE_LESSON)
      });
      if (loggedOut || movedOn) {
        pendingRequest = null;
        attempt = 0;
        offline = false;
        yield put({ type: RECONNECT_DEACTIVATE });
        continue;
      }
      // a fresh connection deserves an immediate attempt, not the backoff earned while offline
      offline = false;
      attempt = 0;
    } else {
      const wait = reconnectDelay(attempt);
      yield put({ type: RECONNECT_WAIT, payload: { retryInSeconds: wait / 1000 } });

      const { loggedOut, wentOffline, movedOn } = yield race({
        automaticReconnect: call(delay, wait),
        manualReconnect: take(RECONNECT_RETRY_NOW),
        backOnline: take(CONNECTION_ONLINE),
        wentOffline: take(CONNECTION_OFFLINE),
        loggedOut: take(LOGOUT),
        movedOn: take(LEFT_THE_LESSON)
      });

      if (loggedOut || movedOn) {
        pendingRequest = null;
        attempt = 0;
        yield put({ type: RECONNECT_DEACTIVATE });
        continue;
      }
      if (wentOffline) {
        offline = true;
        continue;
      }
    }

    yield put({ type: RECONNECT_TRY });

    try {
      // The replay races logout and lesson changes, so a reply that arrives after the learner moved
      // on isn't processed into whatever they are looking at now. Cancelling also keeps the
      // interceptor from starting the flow again for a request that is no longer wanted.
      // A device coming back online mid-attempt is deliberately not raced here: firing a second copy
      // of a request the server may already be handling risks a double submission, and the attempt
      // is bounded by RECONNECT_REQUEST_TIMEOUT anyway.
      const cancellation = CancelToken.source();
      const { retryRequestResponse, loggedOut, movedOn } = yield race({
        retryRequestResponse: call(axiosRetryOn, { ...pendingRequest.payload, cancelToken: cancellation.token }),
        loggedOut: take(LOGOUT),
        movedOn: take(LEFT_THE_LESSON)
      });
      if (loggedOut || movedOn) {
        cancellation.cancel(loggedOut ? 'logout' : 'left the lesson');
        pendingRequest = null;
        attempt = 0;
        yield put({ type: RECONNECT_DEACTIVATE });
        continue;
      }
      yield put({
        type: PROCESS_ACTIONS,
        payload: groupAudios(retryRequestResponse.data)
      });
      yield put({ type: RECONNECT_DEACTIVATE });
      pendingRequest = null;
      attempt = 0;
    } catch (error) {
      // An expired or rejected session won't be fixed by retrying: hand it to the usual logout path
      // instead of counting down forever.
      const status = error.response && error.response.status;
      if (status === 401 || status === 403) {
        yield put(checkCommonErrors(error, { type: RECONNECT_DEACTIVATE }));
        yield put({ type: RECONNECT_DEACTIVATE });
        pendingRequest = null;
        attempt = 0;
        continue;
      }
      // any other failure -- lost connection, timeout, or an error from the server -- goes back to
      // the countdown rather than looping silently on 'Reconnecting...'
      attempt += 1;
      // connection events during the request itself aren't observed by the race above, so ask the
      // device where it stands before deciding to count down
      offline = yield call(isOffline);
    }
  }
}

export function* translateActions(nextActions) {
  let actionsToBeProcessed = nextActions.payload;
  let quickAnswer;

  if (!Array.isArray(actionsToBeProcessed)) {
    actionsToBeProcessed = [actionsToBeProcessed];
  }

  for (let action of actionsToBeProcessed) {
    switch (action.type) {
      case SET_UI_ELEMENT:
        yield put({
          type: UPDATE_UI,
          payload: {
            element: action['ui-element'],
            value: action.value
          }
        });
        break;

      case LOAD_MESSAGES:
        if (config.isDemoMode === false) {
          const result = yield call(apiClient.loadHistory, action.url, 0);
          let historyMessages = _.find(result.data, { type: 'MESSAGE_ACTION' }).payload;
          let historyMetadata = _.find(result.data, { type: 'SET_UI_ELEMENT' });
          yield put({
            type: LOAD_HISTORY,
            payload: historyMessages,
            url: action.url,
            pageNumber: 0,
            scrollTop: false,
            scrollBottom: true
          });
          yield put({
            type: UPDATE_UI,
            payload: {
              element: historyMetadata['ui-element'],
              value: historyMetadata.value
            }
          });
        }
        break;
      case LOAD_NEXT_MODULE_ACTION:
        yield call(loadModule, action.url, action.method);
        break;

      case PLAY_ACTION:
        let url = Array.isArray(action.url) ? action.url : [action.url];
        if (!quickAnswer) {
          yield put({
            type: PLAY_AUDIO,
            payload: {
              url: url,
              autoPlay: action.autoplay,
              quickInputEnable: shouldEnableInputForAction(action, actionsToBeProcessed),
              inputType: getInputType(actionsToBeProcessed),
              scrollable: action.scrollable === undefined ? true : action.scrollable
            }
          });
          if (url.length <= 0) {
            yield put({ type: REMOVE_AUDIO_MODAL });
          }
          ({ quickAnswer } = yield race({
            quickAnswer: take(SUBMIT_ANSWER),
            audioFinished: take(AUDIO_FINISHED)
          }));
        }

        break;

      case PLAY_ACTION_ERROR:
        if (!quickAnswer && action.url && action.url.length > 0) {
          yield put({
            type: PLAY_AUDIO,
            payload: {
              url: Array.isArray(action.url) ? action.url : [action.url],
              autoPlay: action.autoplay,
              quickInputEnable: shouldEnableInputForAction(action, actionsToBeProcessed),
              inputType: getInputType(actionsToBeProcessed),
              scrollable: action.scrollable === undefined ? true : action.scrollable
            }
          });

          ({ quickAnswer } = yield race({
            quickAnswer: take(SUBMIT_ANSWER),
            audioFinished: take(AUDIO_FINISHED)
          }));
        } else {
          yield put({ type: REMOVE_AUDIO_MODAL });
        }

        break;

      case MESSAGE_ACTION:
        yield put({ type: ADD_MESSAGE, payload: action.payload });
        break;
      case MESSAGE_HEADER_ACTION:
        yield put({ type: ADD_HEADER_MESSAGE, payload: action.payload });
        break;

      case CLEAR_MESSAGES_ACTION:
        yield put({ type: CLEAR_MESSAGES });
        break;

      case WAIT_FOR_USER_INPUT_ACTION:
        const validationUrl = action.validateUrl || '/learner/validate-sms';
        // In case an answer has been submitted during the PLAY_AUDIO action
        // proceed and call the server with that data
        if (quickAnswer) {
          yield call(validateAnswer, quickAnswer.payload.answer, validationUrl);
          quickAnswer = null;
        }

        // else continue with the normal answer flow
        else {
          yield put({
            type: ENABLE_INPUT,
            payload: { type: action['input-type'], options: action.options }
          });
          const answer = yield take(SUBMIT_ANSWER);
          yield call(validateAnswer, answer.payload.answer, validationUrl);
        }
        break;

      case REDIRECT:
        const newActions = yield call(apiClient.callRedirect, action.payload);
        yield put({
          type: PROCESS_ACTIONS,
          payload: groupAudios(newActions.data)
        });
        break;

      default:
        if (!action.type) break;
        // some actions we don't need to process under this saga,
        // they can be forwarded directly to state reducers.

        // NOTE: custom controller steps (questionnaires) actions
        // are forwarded through here, see reducers/questionnaires.js
        yield put({ type: action.type, payload: action.payload || {} });
        break;
    }
  }
}

// Will validate the user answer against the server endpoint

export function* validateAnswer(answer, validationUrl) {
  const userId = yield select(getUserId);
  try {
    if (answer.audio) {
      let fileData = new FormData();
      var fileOfBlob = new File([answer.recordedBlob.blob], answer.name);
      fileData.append('file_upload', fileOfBlob);
      const audioFile = yield call(apiClient.uploadFile, fileData);
      let nextActions = yield call(
        apiClient.validateAnswer,
        userId,
        audioFile.data.savedFiles[0].url,
        validationUrl
      );
      yield put({
        type: PROCESS_ACTIONS,
        payload: groupAudios(nextActions.data)
      });
    } else {
      let nextActions = yield call(apiClient.validateAnswer, userId, answer, validationUrl);
      yield put({
        type: PROCESS_ACTIONS,
        payload: groupAudios(nextActions.data)
      });
    }
  } catch (error) {
    let errorPayload = {
      type: VALIDATION_FAILED,
      notification: {
        message: 'Connection problem',
        className: 'error',
        type: 'mainCourse'
      }
    };
    yield put(checkCommonErrors(error, errorPayload));
  }
}

// Will load the next module according the user's state in the course

export function* loadModule(url, method) {
  const userId = yield select(getUserId);
  try {
    const newModule = yield call(apiClient.loadModule, userId, url, method);
    // Load the new module in the UI

    yield put({ type: MODULE_SUCCESSFULLY_LOADED });

    // Send the new action to be processed

    yield put({ type: PROCESS_ACTIONS, payload: groupAudios(newModule.data) });
  } catch (error) {
    let errorPayload = {
      type: MODULE_LOAD_FAILED,
      notification: {
        message: 'Connection problem',
        className: 'error',
        type: 'mainCourse'
      }
    };
    yield put(checkCommonErrors(error, errorPayload));
  }
}

// Loads ui-elements and message history

export function* loadInitialContext(context = {}) {
  const userId = yield select(getUserId);
  try {
    let selectedCourse = window.localStorage.getItem('selectedCourse');
    if (selectedCourse) {
      yield call(apiClient.switchCourse, userId, selectedCourse);
      window.localStorage.removeItem('selectedCourse');
    }
    // browserHistory.push({ pathname: `${config.celledApi.baseUrl}/` });
    const newModule = yield call(apiClient.loadContext, userId, context);
    yield put({ type: PROCESS_ACTIONS, payload: groupAudios(newModule.data) });
    yield call(loadModule);
  } catch (error) {
    let errorPayload = {
      type: MODULE_LOAD_FAILED,
      notification: {
        message: 'Connection problem',
        className: 'error',
        type: 'mainCourse'
      }
    };
    if (
      (error.response && error.response.data && error.response.data.code === 1017) ||
      (error.response && error.response.data && error.response.data.code === 1001)
    ) {
      errorPayload = {
        type: MODULE_LOAD_FAILED,
        notification: {
          message: error.response.data.message,
          className: 'error',
          type: 'mainCourse'
        }
      };

      // TODO-santi: why is this here? is this a retry attempt? what are codes 1017 and 1001?
      browserHistory.push({ pathname: config.celledApi.baseUrl });
      const newModule = yield call(apiClient.loadContext, userId, context);
      yield put({
        type: PROCESS_ACTIONS,
        payload: groupAudios(newModule.data)
      });
      yield call(loadModule);
    }

    yield put(checkCommonErrors(error, errorPayload));
  }
}

// Watcher to load the module when first rendering the web app

export function* loadInitialModule() {
  while (true) {
    yield take(LOAD_CONTEXT);
    yield call(loadInitialContext, getInitialLoadingModuleContext());
  }
}

export function* reloadInitialModule() {
  while (true) {
    yield take(RELOAD_CONTEXT);
    yield put({ type: PROCESS_RELOAD_CONTEXT, reload: true });
    yield call(loadInitialContext);
  }
}

function* callJump(jumpURL) {
  const userId = yield select(getUserId);
  const result = yield call(apiClient.jump, userId, jumpURL);
  yield put({ type: PROCESS_ACTIONS, payload: groupAudios(result.data) });
}

export function* loadHistory() {
  while (true) {
    let payload = yield take(LOAD_HISTORY_NEXT_PAGE);
    let url = payload.history.url;
    let pageNumber = payload.history.pageNumber;
    let result = yield call(apiClient.loadHistory, url, pageNumber);
    let historyMessages = _.find(result.data, { type: 'MESSAGE_ACTION' }).payload;
    let historyMetadata = _.find(result.data, { type: 'SET_UI_ELEMENT' });
    yield put({
      type: LOAD_HISTORY,
      payload: historyMessages,
      url: url,
      pageNumber: pageNumber,
      scrollTop: true,
      scrollBottom: false
    });
    yield put({
      type: UPDATE_UI,
      payload: {
        element: historyMetadata['ui-element'],
        value: historyMetadata.value
      }
    });
  }
}

export default function* rootMainCourseSaga() {
  yield [
    loadInitialModule(),
    reloadInitialModule(),
    processServerActions(),
    reconnectTry(),
    loadHistory()
  ];
}
